// Comparação de versão para o console.
//
// 🔴 Comparar versão como TEXTO é o defeito que esta função existe para fechar (#22). O produto
// carimba a versão com o nome da tag (`v1.6.0`, com o `v`) e o arquivo que anuncia a versão corrente
// diz `1.6.0`, sem — então `!==` dizia "atrasada" para quem estava em dia. Texto também erra o caso
// que mais importa: `1.10.0` é MENOR que `1.9.0` como texto, e maior como versão.
//
// A semântica é deliberadamente a mesma de `src/modules/updates/semver.ts` no produto, porque o selo
// que o cliente vê e o que nós vemos têm de concordar. Em especial: **entrada que não dá para
// interpretar compara como igual**, nunca como atrasada. Um cliente que hospeda pode carimbar
// qualquer coisa na versão, e inventar que ele está para trás é pior do que não saber.

export type Versao = [number, number, number];

export function analisar(v: string): Versao | null {
  const base = v.trim().replace(/^v/i, "").split(/[-+]/, 1)[0] ?? "";
  if (!base) return null;
  const partes = base.split(".").slice(0, 3);
  const nums: number[] = [];
  for (const p of partes) {
    // Segmento vazio ou não numérico recusa: `Number("")` é 0, e "" viraria 0.0.0 em silêncio.
    if (!/^\d+$/.test(p)) return null;
    nums.push(Number(p));
  }
  return [nums[0] ?? 0, nums[1] ?? 0, nums[2] ?? 0];
}

// -1 se a < b, 1 se a > b, 0 se iguais OU se algum lado não puder ser interpretado.
export function comparar(a: string, b: string): number {
  const pa = analisar(a);
  const pb = analisar(b);
  if (!pa || !pb) return 0;
  for (let i = 0; i < 3; i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

// Verdadeiro só quando `corrente` é comprovadamente mais antiga que `ultima`. Qualquer dúvida —
// versão desconhecida, hub sem internet, carimbo estranho — responde falso.
export function estaAtrasada(
  corrente: string | null | undefined,
  ultima: string | null | undefined,
): boolean {
  if (!corrente || !ultima) return false;
  return comparar(corrente, ultima) < 0;
}

// A mais nova de uma lista, para mostrar. Devolve o texto ORIGINAL (não o normalizado), porque o que
// a instalação carimbou é o que o suporte vai procurar nos registros dela.
export function maisNova(versoes: readonly (string | null)[]): string | null {
  let melhor: string | null = null;
  for (const v of versoes) {
    if (!v) continue;
    if (melhor === null || comparar(v, melhor) > 0) melhor = v;
  }
  return melhor;
}

// Exibe com um `v` e só um: a origem às vezes já traz o prefixo, e foi assim que nasceu o `vv1.6.0`.
export function paraMostrar(v: string | null | undefined, vazio = "—"): string {
  if (!v) return vazio;
  const limpo = v.trim().replace(/^v/i, "");
  return limpo ? `v${limpo}` : vazio;
}
